import { cn } from "@/utils";
import { useCallback, useEffect, useRef, type ReactNode } from "react";
import {
  CANVAS_GLYPH_STROKE,
  CANVAS_TILE_GLYPH,
} from "./canvasToolbarComposition";
import { canvasToolbarTileVariants } from "./canvasToolbarStyles";

export type CanvasToolPopoverCloseReason = "escape" | "outside" | "leave";

interface CanvasToolPopoverProps {
  open: boolean;
  onClose: (reason: CanvasToolPopoverCloseReason) => void;
  label: string;
  /** The trigger tile's centre, measured from the bar's left edge. Beside
   * the column the stack stands on the column's foot instead. */
  anchor: number;
  /** `right`: beside the fit shed's vertical column. */
  side?: "top" | "right";
  children: ReactNode;
}

/**
 * The bar's small disclosure — exp-canvas-toolbar `.xtp-`: one row of picks
 * with nothing to configure beneath, floating 8px over the bar, centred on
 * its trigger, closed on all four corners. It is the bar, smaller: the same
 * n-05 shell at radius 12 around an n-00 well at radius 8 holding 40px
 * tiles, one elevation rung under the bar because it is the bar's content.
 * It dismisses the way a popover does — pointer-leave, Escape, or a press
 * anywhere else — and never takes focus on open, so a keyboard user is not
 * stranded in a surface that closes when the pointer leaves. Beside the
 * vertical column it turns with the column: a stack, top to bottom, 8px
 * right of it and standing on its foot beside the trigger.
 */
export function CanvasToolPopover({
  open,
  onClose,
  label,
  anchor,
  side = "top",
  children,
}: CanvasToolPopoverProps) {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      e.stopPropagation();
      onClose("escape");
    };
    // pointerdown, not click, so it fires before the pressed thing takes
    // focus; the trigger dismisses itself, so letting this fire too would
    // close and reopen on one press.
    const onDown = (e: PointerEvent) => {
      const el = ref.current;
      const target = e.target as HTMLElement | null;
      if (!el || !target || el.contains(target)) return;
      if (target.closest?.("[data-popover-trigger]")) return;
      onClose("outside");
    };
    document.addEventListener("keydown", onKey);
    document.addEventListener("pointerdown", onDown);
    return () => {
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("pointerdown", onDown);
    };
  }, [open, onClose]);

  const onLeave = useCallback(() => onClose("leave"), [onClose]);

  if (!open) return null;

  return (
    <div
      ref={ref}
      role="group"
      aria-label={label}
      data-canvas-control
      data-side={side}
      style={side === "top" ? { left: anchor } : undefined}
      className={cn(
        "absolute z-(--z-dropdown) flex w-max items-center",
        "before:absolute before:content-['']",
        "rounded-xl border border-n-10 bg-n-05 p-1 shadow-elev-4 dark:border-n-40 dark:bg-n-30",
        "animate-in fade-in-0 duration-200 ease-out motion-reduce:animate-none",
        // The ::before spans the gap to the bar, so the reach never crosses
        // the canvas.
        side === "top"
          ? "bottom-[calc(100%+var(--space-2))] -translate-x-1/2 slide-in-from-bottom-1 before:inset-x-0 before:top-full before:h-2"
          : "bottom-0 left-[calc(100%+var(--space-2))] slide-in-from-left-1 before:inset-y-0 before:right-full before:w-2",
      )}
      onPointerLeave={onLeave}
      onClick={(e) => e.stopPropagation()}
    >
      <div
        className={cn(
          "flex items-center gap-0.5 rounded-lg bg-n-00",
          side === "right" && "flex-col",
        )}
      >
        {children}
      </div>
    </div>
  );
}

interface CanvasToolPopoverItemProps {
  label: string;
  /** The key that arms it, appended to the tooltip only. */
  hint?: string;
  pressed: boolean;
  icon: React.ComponentType<{
    size?: number;
    strokeWidth?: number;
    "aria-hidden"?: boolean | "true";
  }>;
  onSelect: () => void;
}

/** A pick inside the popover: the bar's own tile at the tray tile's radius. */
export function CanvasToolPopoverItem({
  label,
  hint,
  pressed,
  icon: Icon,
  onSelect,
}: CanvasToolPopoverItemProps) {
  return (
    <button
      type="button"
      className={cn(canvasToolbarTileVariants(), "rounded-md")}
      aria-pressed={pressed}
      aria-label={label}
      title={hint ? `${label} · ${hint}` : label}
      onClick={onSelect}
    >
      <Icon
        size={CANVAS_TILE_GLYPH}
        strokeWidth={CANVAS_GLYPH_STROKE}
        aria-hidden="true"
      />
    </button>
  );
}
